<script setup lang="ts">
    import {inject} from "vue";

    import {useModal} from "../composables/useModal";

    import DButton from "../atoms/DButton.vue";
    import DContainer from "../atoms/DContainer.vue";

    import DVideoModal from "../overlays/DVideoModal.vue";

    import type {
        NormalizedCTA,
        ComponentThemes,
        ButtonSizes,
        TrackClickEventType,
    } from "@songfinch/types";

    const props = withDefaults(
        defineProps<{
            variant?: "static" | "float" | "center";
            spacing?: "space-top" | "space-top-short";
            size?: ButtonSizes;
            internalTitle?: string;
            dataTestId: string;
            theme?: ComponentThemes;
            cta: NormalizedCTA;
        }>(),
        {
            variant: "static",
            dataTestId: "DCTA",
        },
    );
    const {modalActive, openModal, closeModal} = useModal({
        trackingProperties: {
            type: "video_opened",
            payload: {
                video_title: props?.cta?.video?.title || "",
            },
        },
    });

    const openMediaVideo = (e: MouseEvent) => {
        if (!props.cta.video) return;
        openModal(e);
    };

    const trackClickEvent = inject<TrackClickEventType | null>("trackClickEvent", null);
</script>

<template>
    <DContainer
        class="cta-link-container"
        :class="`${variant || ''} ${spacing || ''}`"
    >
        <DButton
            v-if="cta.type === 'link' && cta.link"
            class="cta-link"
            type="link"
            :variant="cta.variant"
            :size="size"
            :theme="theme"
            :data-test-id="`${dataTestId}-link`"
            :to="cta.link.externalLink || cta.link.slug"
            :label="cta.text"
            @click="trackClickEvent"
        />
        <DButton
            v-if="cta.type === 'videoModal'"
            class="cta-link"
            :variant="cta.variant"
            :size="size"
            :theme="theme"
            :data-test-id="`${dataTestId}-video-modal`"
            :label="cta.text"
            @click="openMediaVideo"
        />
        <DButton
            v-if="cta.type === 'button'"
            class="cta-link"
            :variant="cta.variant"
            :size="size"
            :theme="theme"
            :data-test-id="`${dataTestId}-event`"
            :label="cta.text"
            @click="trackClickEvent"
        />
    </DContainer>

    <Teleport v-if="cta.type === 'videoModal' && cta.video" to="body">
        <DVideoModal
            v-if="modalActive"
            :data-test-id="`${dataTestId}-video-modal`"
            :src="cta.video.url"
            :title="cta.video.title"
            @close="closeModal"
        />
    </Teleport>
</template>

<style lang="scss" scoped>
.space-top-short {
  margin-top: 1rem;
}

.space-top {
  margin-top: 3rem;
}

.center {
  width: auto;
  margin: 0 auto;
}

.static {
  display: inline-flex;
  width: auto;

  @include md {
    display: grid;
    justify-content: flex-start;
  }
}

.float {
  display: flex;
  width: calc(100vw - 2rem);
  margin-inline: auto;

  .cta-link {
    width: calc(100vw - 2rem);
  }

  @include md {
    display: grid;
    justify-content: center;

    .cta-link {
      width: initial;
      margin-inline: initial;
    }
  }
}
</style>
